import {
  ColumnDefinition,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiDataTableComponent,
  OuiDialogComponent,
  OuiDividerComponent,
  OuiGridComponent,
  NotificationService,
  OuiPaginatorComponent,
  OuiServerSideDatasource as OuiDatasource,
  OuiRowComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, signal, TemplateRef, untracked, viewChild, viewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { InputComponent } from '../../components/input/input.component';
import { Option } from '../../models/common.models';
import { OrderDevicesRequest, OrderHistoryDetailItem, OrderHistorySummary } from '../../models/order.models';
import { ConfigMappingResponse, CreateRmaRequest, MyWarehouseChannelsResponse, PaginatedRmaByAtbOrderResponse, RmaByAtbOrderItemResponse, RmaDetailItemResponse, RmaDetailResponse } from '../../models/rma.models';
import { SelectComponent } from '../../components/select/select.component';
import { OrderService } from '../../services/order/order.service';
import { RmaService } from '../../services/rma/rma.service';
import { SalesChannelService } from '../../services/sales-channel/sales-channel.service';
import { BasePageComponent } from '../base/base.component';
import { RmaReturnSectionComponent } from './components/rma-return-section/rma-return-section.component';

type ExistingRmaRow = {
  rmaId: number;
  rmaNumber: string;
  type: string;
  created: string;
  reason: string;
  value: string;
  status: string;
};

type ReturnDeviceRow = {
  id: string;
  device: string;
  imei: string;
  sku: string;
  capacity: string;
  unitPrice: number;
  skuDescription: string;
  sellPrice: string;
  sellPriceAmount: number;
};

type ReviewReturnDeviceRow = {
  id: string;
  device: string;
  imei: string;
  sku: string;
  capacity: string;
  unitPrice: number;
  skuDescription: string;
  sellPrice: string;
  creditValue: string;
};

type ReviewReturnSection = {
  returnNumber: number;
  reason: string;
  comments: string;
  attachments: Array<{
    name: string;
    type: string;
    size: string;
  }>;
  totalCreditValue: string;
  devices: ReviewReturnDeviceRow[];
};

type SubmitRmaItem = {
  sku: string;
  serial_number: string;
  reason_code: string;
  comments: string;
  capacity: string;
  unit_price: number;
  credit_amount: number | null;
  sku_description: string;
};

type ExistingRmaDetailDeviceRow = {
  device: string;
  imei: string;
  sku: string;
  condition: string;
  compliance: string;
};

type ExistingRmaActivityItem = {
  title: string;
  subtitle: string;
  variant: string;
};

@Component({
  selector: 'app-rma-create',
  standalone: true,
  imports: [
    OuiBoxComponent,
    OuiButtonComponent,
    OuiCenterComponent,
    OuiDataTableComponent,
    OuiDialogComponent,
    OuiDividerComponent,
    OuiGridComponent,
    OuiPaginatorComponent,
    OuiRowComponent,
    OuiStackComponent,
    OuiTextComponent,
    InputComponent,
    RmaReturnSectionComponent,
    SelectComponent,
    RouterLink,
    ReactiveFormsModule,
  ],
  templateUrl: './rma-create.component.html',
  styleUrl: './rma-create.component.css',
})
export class RmaCreatePage extends BasePageComponent {
  private static readonly ALL_CHANNEL_VALUE = '__all__';

  readonly selectionCell = viewChild<TemplateRef<never>>('selectionCell');
  readonly rmaCountCell = viewChild<TemplateRef<never>>('rmaCountCell');
  readonly existingRmaNumberCell = viewChild<TemplateRef<never>>('existingRmaNumberCell');
  readonly existingRmaStatusCell = viewChild<TemplateRef<never>>('existingRmaStatusCell');
  readonly deviceSelectionCell = viewChild<TemplateRef<never>>('deviceSelectionCell');
  readonly creditValueCell = viewChild<TemplateRef<never>>('creditValueCell');
  readonly creditDeviceCell = viewChild<TemplateRef<never>>('creditDeviceCell');
  readonly creditImeiCell = viewChild<TemplateRef<never>>('creditImeiCell');
  readonly creditSellPriceCell = viewChild<TemplateRef<never>>('creditSellPriceCell');
  readonly returnSections = viewChildren(RmaReturnSectionComponent);

  private readonly orders = inject(OrderService);
  private readonly rmaService = inject(RmaService);
  private readonly salesChannelService = inject(SalesChannelService);
  private readonly notifications = inject(NotificationService);

  readonly activeRegionId = this.regions.selectedRegionId;
  private readonly myWarehouseChannels = signal<MyWarehouseChannelsResponse | null>(null);

  readonly channelOptions = computed<Option[]>(() => {
    const channels = this.myWarehouseChannels();
    const allSalesChannels = this.salesChannelService.salesChannelOptions();
    if (!channels) {
      return [{ label: 'ALL', value: RmaCreatePage.ALL_CHANNEL_VALUE }];
    }

    const channelOpts = [...new Set(channels.can_create.map((e) => e.channel_code))].map((code) => {
      const match = allSalesChannels.find((o) => String(o.value) === code);
      return { label: match ? String(match.label) : code, value: code };
    });
    return [{ label: 'ALL', value: RmaCreatePage.ALL_CHANNEL_VALUE }, ...channelOpts];
  });

  readonly defaultChannelValue = computed(() => {
    return RmaCreatePage.ALL_CHANNEL_VALUE;
  });

  readonly currentStep = signal<1 | 2 | 3>(1);
  readonly returnSectionIds = signal<number[]>([1]);
  readonly selectedDeviceImeisByReturn = signal<Record<number, string[]>>({});
  readonly disabledImeisByReturn = computed<Record<number, string[]>>(() => {
    const selected = this.selectedDeviceImeisByReturn();
    const sectionIds = this.returnSectionIds();
    const existingImeis = this.existingRmaDeviceImeis();

    return sectionIds.reduce<Record<number, string[]>>((result, sectionId, index) => {
      const disabled = [...existingImeis];
      for (const previousSectionId of sectionIds.slice(0, index)) {
        disabled.push(...(selected[previousSectionId] || []));
      }
      result[sectionId] = [...new Set(disabled)];
      return result;
    }, {});
  });
  readonly selectedOrderId = signal<number | null>(null);
  readonly selectedOrder = signal<OrderHistorySummary | null>(null);
  readonly existingRmaMessage = signal<string>('');
  readonly showExistingRmaDialog = signal(false);
  readonly existingRmaDetailLoading = signal(false);
  readonly existingRmaDetailError = signal('');
  readonly existingRmaDetail = signal<RmaDetailResponse | null>(null);
  readonly existingRmaActivityItems = signal<ExistingRmaActivityItem[]>([]);
  readonly startReturnType = signal<'regular' | 'credit'>('regular');
  readonly reviewReturnSections = signal<ReviewReturnSection[]>([]);
  readonly submittingRma = signal(false);
  readonly showSubmitSuccessDialog = signal(false);
  readonly createdRmaNumber = signal('');
  readonly orderHistoryDetailItems = signal<OrderHistoryDetailItem[]>([]);
  readonly defaultOrderHistoryDetailItems = signal<OrderHistoryDetailItem[]>([]);
  readonly selectedDeviceIds = signal<string[]>([]);
  readonly devicesMessage = signal<string>('');
  readonly creditValueByDeviceId = signal<Record<string, number>>({});
  readonly creditInputByDeviceId = signal<Record<string, string>>({});
  readonly creditValidationByDeviceId = signal<Record<string, string>>({});
  readonly bulkCreditValue = signal<string>('');
  readonly selectedAttachmentFiles = signal<File[]>([]);
  readonly attachmentErrorMessage = signal('');
  readonly isAttachmentDragActive = signal(false);
  readonly attachmentAllowedFileExtensions = ['pdf', 'jpg', 'png', 'doc', 'xls'];
  readonly existingRmaDeviceImeis = signal<string[]>([]);
  readonly deviceListLoading = signal(false);

  private readonly maxAttachmentSizeBytes = 10 * 1024 * 1024;
  private returnSectionIdSeed = 1;
  private activeStartReturnOrderKey: string | null = null;
  private existingRmaImeiRequestId = 0;
  private existingRmasFirstPageCache: { atbId: number; response: PaginatedRmaByAtbOrderResponse } | null = null;
  private lastLoadedRegionId?: number;

  matchingOrderColumns!: ColumnDefinition<OrderHistorySummary>[];
  existingRmaColumns!: ColumnDefinition<ExistingRmaRow>[];
  returnDeviceColumns!: ColumnDefinition<ReturnDeviceRow>[];
  creditValueColumns!: ColumnDefinition<ReturnDeviceRow>[];

  readonly matchingOrdersDatasource = new OuiDatasource<OrderHistorySummary>(
    (request) => this.orders.searchOrdersForRma(request, this.getWarehouseChannelCombinations()),
    10,
  );

  readonly existingRmasDatasource = new OuiDatasource<ExistingRmaRow>(
    async (request) => {
      const atbId = Number((request.filter as { atb_Id?: number } | undefined)?.atb_Id);

      if (!atbId) {
        this.existingRmaMessage.set('');
        return {
          data: [],
          totalRecords: 0,
          pageNumber: request.pageNumber || 1,
          pageSize: request.pageSize || 10,
        };
      }

      try {
        const pageNumber = request.pageNumber || 1;
        const pageSize = request.pageSize || 10;
        const sortBy = request.sortColumn === 'created' ? 'created_on' : request.sortColumn || 'created_on';
        const sortDirection =
          request.sortDirection && request.sortDirection !== 'indet'
            ? request.sortDirection
            : 'desc';
        const cachedResponse =
          this.existingRmasFirstPageCache?.atbId === atbId &&
          pageNumber === 1 &&
          pageSize === this.existingRmasFirstPageCache.response.page_size &&
          sortBy === 'created_on' &&
          sortDirection === 'desc'
            ? this.existingRmasFirstPageCache.response
            : null;
        const response =
          cachedResponse ||
          (await this.rmaService.getRmasByAtbOrder({
            atb_Id: atbId,
            pageNumber,
            pageSize,
            sortBy,
            sortDirection,
          }));

        this.existingRmaMessage.set('');

        return {
          data: response.items.map((item) => this.mapExistingRmaRow(item)),
          totalRecords: response.total_count,
          pageNumber: response.page_number,
          pageSize: response.page_size,
        };
      } catch (error) {
        this.existingRmaMessage.set(this.getExistingRmaErrorMessage(error));

        return {
          data: [],
          totalRecords: 0,
          pageNumber: request.pageNumber || 1,
          pageSize: request.pageSize || 10,
        };
      }
    },
    10,
  );

  readonly returnDevicesDatasource = new OuiDatasource<ReturnDeviceRow>(
    async (request) => {
      const rows = this.getReturnDeviceRows();
      const filtered = rows;

      const pageSize = request.pageSize || 8;
      const pageNumber = request.pageNumber || 1;
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: filtered.slice(start, end),
        totalRecords: filtered.length,
        pageNumber,
        pageSize,
      };
    },
    8,
  );

  readonly creditValuesDatasource = new OuiDatasource<ReturnDeviceRow>(
    async (request) => {
      const rows = this.getSelectedDeviceRows();
      const pageSize = request.pageSize || 8;
      const pageNumber = request.pageNumber || 1;
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: rows.slice(start, end),
        totalRecords: rows.length,
        pageNumber,
        pageSize,
      };
    },
    8,
  );

  readonly searchForm = new FormGroup({
    channel: new FormControl(''),
    startDate: new FormControl(''),
    endDate: new FormControl(''),
    orderNumber: new FormControl(''),
  });

  readonly startReturnForm = new FormGroup({
    reason: new FormControl(''),
    comments: new FormControl(''),
  });

  readonly reasonOptions = signal<Array<{ label: string; value: string }>>([]);

  readonly searchFormValue = toSignal(this.searchForm.valueChanges);
  readonly returnSectionDeviceRows = computed(() => this.getReturnDeviceRows());
  readonly endDateMinValue = computed(() => {
    const valueChanges = this.searchFormValue();
    const startDate = valueChanges?.startDate;
    return typeof startDate === 'string' && startDate.length > 0
      ? startDate
      : undefined;
  });
  readonly existingRmaDetailDeviceRows = computed<ExistingRmaDetailDeviceRow[]>(() => {
    const detail = this.existingRmaDetail();
    if (!detail) {
      return [];
    }

    return (detail.rma_items || []).map((item) => ({
      device: this.resolveExistingRmaDeviceName(item),
      imei: item.serial_number || '-',
      sku: item.sku || '-',
      condition: this.toLabel(item.reason_code || '').replace(/_/g, ' ') || '-',
      compliance: item.compliance_status || '-',
    }));
  });

  constructor() {
    super();

    void this.loadReturnReasonOptions();

    effect(() => {
      this.matchingOrderColumns = [
        {
          columnDef: 'orderId',
          header: '',
          template: this.selectionCell(),
        },
        {
          columnDef: 'webOrderId',
          header: 'Order Number',
          isSticky: true,
        },
        {
          columnDef: 'shipmentId',
          header: 'Shipment Number',
        },
        {
          columnDef: 'buyerCompanyName',
          header: 'Customer',
        },
        {
          columnDef: 'salesChannelCode',
          header: 'Channel',
          cell: (row) => row.salesChannelCode || '-',
        },
        {
          columnDef: 'orderDate',
          header: 'Date & Time',
          cell: (row) => this.getDateTime(row.orderDate),
        },
        {
          columnDef: 'totalQty',
          header: 'Qty',
        },
        {
          columnDef: 'rmaCount',
          header: 'RMAs',
          template: this.rmaCountCell(),
        },
      ];
    });

    effect(() => {
      this.existingRmaColumns = [
        {
          columnDef: 'rmaNumber',
          header: 'RMA #',
          template: this.existingRmaNumberCell(),
        },
        {
          columnDef: 'type',
          header: 'Type',
        },
        {
          columnDef: 'created',
          header: 'Created',
        },
        {
          columnDef: 'reason',
          header: 'Reason',
        },
        {
          columnDef: 'value',
          header: 'Value',
        },
        {
          columnDef: 'status',
          header: 'Status',
          template: this.existingRmaStatusCell(),
        },
      ];
    });

    effect(() => {
      this.returnDeviceColumns = [
        {
          columnDef: 'id',
          header: '',
          template: this.deviceSelectionCell(),
        },
        {
          columnDef: 'device',
          header: 'Device',
          textAlign: 'left',
        },
        {
          columnDef: 'imei',
          header: 'IMEI',
          textAlign: 'left',
        },
        {
          columnDef: 'sku',
          header: 'SKU',
          textAlign: 'left',
        },
        {
          columnDef: 'sellPrice',
          header: 'Sell Price',
          textAlign: 'right',
        },
      ];
    });

    effect(() => {
      this.creditValueColumns = [
        {
          columnDef: 'device',
          header: 'Device',
          template: this.creditDeviceCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'imei',
          header: 'IMEI',
          template: this.creditImeiCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'sku',
          header: 'SKU',
          textAlign: 'left',
        },
        {
          columnDef: 'sellPrice',
          header: 'Sell Price',
          template: this.creditSellPriceCell(),
          textAlign: 'right',
        },
        {
          columnDef: 'id',
          header: 'Credit Value',
          template: this.creditValueCell(),
          textAlign: 'right',
        },
      ];
    });

    effect(() => {
      const currentChannel = this.searchForm.controls.channel.value || '';
      const defaultChannel = this.defaultChannelValue();

      if (!currentChannel && defaultChannel) {
        this.searchForm.controls.channel.setValue(defaultChannel);
      }
    });

    effect(() => {
      const regionId = this.activeRegionId();
      if (!regionId) {
        return;
      }

      const isRegionChange =
        this.lastLoadedRegionId !== undefined && this.lastLoadedRegionId !== regionId;
      this.lastLoadedRegionId = regionId;

      if (isRegionChange) {
        if (this.currentStep() > 1) {
          this.notifications.showAlert({
            variant: 'warn',
            symbolName: 'report',
            message: 'Changing the region cleared the current Create RMA progress.',
          });
        }
        this.resetForRegionChange();
      }

      this.myWarehouseChannels.set(null);
      void this.rmaService.getMyWarehouseChannels()
        .then((result) => {
          this.myWarehouseChannels.set(result);
          this.searchOrders();
        })
        .catch(() => {
          this.myWarehouseChannels.set(null);
          this.searchOrders();
        });
    });

    this.matchingOrdersDatasource.sortRecords({
      column: 'orderDate',
      direction: 'desc',
    });
    this.existingRmasDatasource.sortRecords({
      column: 'created',
      direction: 'desc',
    });
  }

  clearFilters() {
    this.searchForm.patchValue({
      channel: this.defaultChannelValue(),
      startDate: '',
      endDate: '',
      orderNumber: '',
    });

    this.searchOrders();
  }

  searchOrders() {
    const regionId = this.activeRegionId();
    const channels = untracked(() => this.myWarehouseChannels());
    const createEntries = channels?.can_create ?? [];
    const selectedChannel = this.getSalesChannelFilterValue();
    const isAll = !selectedChannel;

    const warehouse = [...new Set(createEntries.map((e) => e.warehouse_code))];

    let salesChannelCode: string[];
    if (isAll) {
      salesChannelCode = [...new Set(createEntries.map((e) => e.channel_code))];
    } else {
      salesChannelCode = [selectedChannel];
    }

    this.matchingOrdersDatasource.setFilter({
      orderNumber: (this.searchForm.controls.orderNumber.value || '').trim(),
      startDate: this.searchForm.controls.startDate.value || '',
      endDate: this.searchForm.controls.endDate.value || '',
      salesChannelCode: salesChannelCode.length > 0 ? salesChannelCode : null,
      warehouse: warehouse.length > 0 ? warehouse : undefined,
      regionId,
      includeRmaCount: true,
    });

    this.selectedOrderId.set(null);
    this.selectedOrder.set(null);
    this.activeStartReturnOrderKey = null;
    this.existingRmaImeiRequestId += 1;
    this.existingRmaDeviceImeis.set([]);
    this.resetStartReturnDraftState();
  }

  private getSalesChannelFilterValue(): string | null {
    const channelValue = this.searchForm.controls.channel.value;
    if (!channelValue || channelValue === RmaCreatePage.ALL_CHANNEL_VALUE) {
      return null;
    }

    return channelValue;
  }

  private getWarehouseChannelCombinations(): Array<{ warehouseCode: string; channelCode: string }> {
    const channels = untracked(() => this.myWarehouseChannels());
    const createEntries = channels?.can_create ?? [];
    const seen = new Set<string>();

    return createEntries.reduce<Array<{ warehouseCode: string; channelCode: string }>>((acc, entry) => {
      const warehouseCode = entry.warehouse_code;
      const channelCode = entry.channel_code;
      const key = `${warehouseCode}::${channelCode}`;

      if (!warehouseCode || !channelCode || seen.has(key)) {
        return acc;
      }

      seen.add(key);
      acc.push({ warehouseCode, channelCode });
      return acc;
    }, []);
  }

  selectOrder(row: OrderHistorySummary) {
    this.selectedOrderId.set(row.orderId);
    this.selectedOrder.set(row);
  }

  async startReturn() {
    const selectedOrderId = this.selectedOrderId();
    const selectedOrder = this.selectedOrder();

    if (!selectedOrderId || !selectedOrder) {
      return;
    }

    const selectedOrderKey = this.buildStartReturnOrderKey(selectedOrder);
    if (this.activeStartReturnOrderKey !== selectedOrderKey) {
      this.resetStartReturnDraftState();
      this.activeStartReturnOrderKey = selectedOrderKey;
    }

    this.existingRmaMessage.set('');
    this.existingRmaDeviceImeis.set([]);

    if ((selectedOrder.rmaCount || 0) > 0) {
      await this.loadExistingRmaImeiBlocklist(selectedOrderId);
      this.existingRmasDatasource.setFilter({ atb_Id: selectedOrderId });
    } else {
      this.existingRmasDatasource.setFilter({});
    }
    this.currentStep.set(2);
  }

  async onAddDevicesRequested() {
    const selectedOrder = this.selectedOrder();
    if (!selectedOrder) {
      this.devicesMessage.set('Select an order before adding devices.');
      return;
    }

    await this.loadOrderHistoryDetails({
      pageNumber: 1,
      pageSize: 10,
      sortBy: 'serialNumber',
      sortDirection: 'desc',
      order_Id: String(selectedOrder.webOrderId),
      shipment_Id: selectedOrder.shipmentId,
      filters: {
        sku: '',
        serial_number: '',
        sku_description: '',
      },
    });

    this.defaultOrderHistoryDetailItems.set(this.orderHistoryDetailItems());
  }

  async onDeviceSearchRequested(searchTerm: string) {
    const selectedOrder = this.selectedOrder();
    if (!selectedOrder) {
      this.devicesMessage.set('Select an order before searching devices.');
      return;
    }

    const term = (searchTerm || '').trim();
    if (term.length === 0) {
      this.orderHistoryDetailItems.set(this.defaultOrderHistoryDetailItems());
      this.devicesMessage.set('');
      return;
    }

    if (term.length < 3) {
      return;
    }

    await this.loadOrderHistoryDetails({
      pageNumber: 1,
      pageSize: 10,
      sortBy: 'serialNumber',
      sortDirection: 'desc',
      order_Id: String(selectedOrder.webOrderId),
      shipment_Id: selectedOrder.shipmentId,
      filters: {
        sku: term,
        serial_number: term,
        sku_description: term,
      },
    });
  }

  goToSearchAndSelect() {
    this.currentStep.set(1);
    this.reviewReturnSections.set([]);
    this.showSubmitSuccessDialog.set(false);
    this.createdRmaNumber.set('');
  }

  getDateTime(date: Date): string {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  getDateOnly(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  }

  getDisplayOrderNumber(order: OrderHistorySummary): string {
    const webOrderId = (order.webOrderId || '').trim();
    const shipmentId = (order.shipmentId || '').trim();

    if (webOrderId && shipmentId) {
      return `${webOrderId} - ${shipmentId}`;
    }

    return webOrderId || shipmentId || '-';
  }

  getTodaysDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  setStartReturnType(type: 'regular' | 'credit') {
    this.startReturnType.set(type);
  }

  onReviewRma() {
    const sections = this.returnSections();
    const hasInvalidSection = sections.some((section) => !section.validateForReview());
    const hasInvalidCreditSection =
      this.startReturnType() === 'credit' &&
      sections.some((section) => section.hasInvalidCreditValuesForReview());
    const hasMissingDeviceSection = sections.some((section) => !section.hasSelectedDevicesForReview());

    if (hasMissingDeviceSection) {
      this.notifications.showAlert({
        variant: 'warn',
        symbolName: 'report',
        message: 'Select at least one device in each return before reviewing RMA.',
      });
      return;
    }

    if (hasInvalidSection) {
      if (hasInvalidCreditSection) {
        this.notifications.showAlert({
          variant: 'warn',
          symbolName: 'report',
          message: 'Credit Value is required for all selected devices in Credit RMA sections.',
        });
      }
      return;
    }

    this.reviewReturnSections.set(
      this.returnSections().map((section, index) => {
        const sectionData = section.getReviewData();

        return {
          returnNumber: index + 1,
          reason: sectionData.reason,
          comments: sectionData.comments,
          attachments: sectionData.attachments,
          totalCreditValue: sectionData.totalCreditValue,
          devices: sectionData.devices.map((device) => ({
            ...device,
            capacity: device.capacity,
            unitPrice: device.unitPrice,
            skuDescription: device.skuDescription,
          })),
        };
      }),
    );

    this.currentStep.set(3);
  }

  goBackToStartReturn() {
    this.currentStep.set(2);
  }

  async submitRma() {
    if (this.submittingRma()) {
      return;
    }

    const payload = this.buildCreateRmaPayload();
    if (!payload) {
      this.notifications.showAlert({
        variant: 'warn',
        symbolName: 'report',
        message: 'Unable to submit RMA. Missing order or selected item details.',
      });
      return;
    }

    this.submittingRma.set(true);

    try {
      const attachmentFiles = this.getAllAttachmentFiles();
      const response = await this.rmaService.createRma(payload);

      if (attachmentFiles.length > 0) {
        if (typeof response.rma_id !== 'number') {
          throw new Error('RMA created, but attachment upload failed because rma_id was not returned.');
        }

        const rmaId = response.rma_id;
        await this.rmaService.uploadRmaAttachments(rmaId, attachmentFiles);
      }

      const rmaNumber =
        response.rma_number ||
        (typeof response.rma_id === 'number' ? `RMA-${response.rma_id}` : 'RMA-Generated');

      this.resetAfterSuccessfulSubmission();
      this.createdRmaNumber.set(rmaNumber);
      this.showSubmitSuccessDialog.set(true);
    } catch (error) {
      const isUnauthorized = error instanceof HttpErrorResponse && [401, 403].includes(error.status);
      const message = isUnauthorized
        ? 'You are not authorized to create an RMA.'
        : this.getSubmitRmaErrorMessage(error);

      this.notifications.showAlert({
        variant: 'warn',
        symbolName: 'report',
        message,
      });
    } finally {
      this.submittingRma.set(false);
    }
  }

  closeSubmitSuccessDialog() {
    this.showSubmitSuccessDialog.set(false);
    this.goToSearchAndSelect();
  }

  private getSubmitRmaErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const payload = error.error;

      if (typeof payload === 'string' && payload.trim().length > 0) {
        return payload;
      }

      if (payload && typeof payload === 'object') {
        const payloadMessage = payload['message'] ?? payload['error'];
        if (typeof payloadMessage === 'string' && payloadMessage.trim().length > 0) {
          return payloadMessage;
        }
      }

      if (error.message.trim().length > 0) {
        return error.message;
      }
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }

    return 'Failed to submit RMA. Please try again.';
  }

  getAttachmentSummary(attachments: string[]): string {
    if (attachments.length === 0) {
      return 'None';
    }

    return attachments.join(', ');
  }

  async openExistingRmaDetails(rmaId: number) {
    if (!rmaId) {
      return;
    }

    this.showExistingRmaDialog.set(true);
    this.existingRmaDetailLoading.set(true);
    this.existingRmaDetailError.set('');
    this.existingRmaDetail.set(null);
    this.existingRmaActivityItems.set([]);

    try {
      const detail = await firstValueFrom(this.rmaService.getRmaById(rmaId));
      this.existingRmaDetail.set(detail || null);

      if (detail?.rma_number) {
        try {
          const response = await firstValueFrom(
            this.rmaService.getRmaActivityLogs(detail.rma_number),
          );
          const rows = this.getExistingRmaActivityRows(response);
          const seenActivities = new Set<string>();
          this.existingRmaActivityItems.set(
            rows.reduce<ExistingRmaActivityItem[]>((items, row) => {
              const title = this.pickExistingRmaActivityString(row, ['action']) || '-';
              const timestamp = this.pickExistingRmaActivityString(row, ['timestamp']);
              const activityKey = `${title.trim().toLowerCase()}|${timestamp.trim()}`;

              if (seenActivities.has(activityKey)) {
                return items;
              }

              seenActivities.add(activityKey);
              items.push({
                title,
                subtitle: this.formatExistingRmaDate(timestamp),
                variant: this.getExistingRmaActivityVariant(title),
              });
              return items;
            }, []),
          );
        } catch {
          this.existingRmaActivityItems.set([]);
        }
      }
    } catch {
      this.existingRmaDetailError.set('Unable to load RMA details.');
    } finally {
      this.existingRmaDetailLoading.set(false);
    }
  }

  closeExistingRmaDialog() {
    this.showExistingRmaDialog.set(false);
  }

  getExistingRmaOrder(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    const webOrderId = order?.['web_order_id'] ?? source['web_order_id'] ?? source['order_number'];
    const shipmentId =
      order?.['shipment_id'] ??
      order?.['shipment_number'] ??
      order?.['shipment_Id'] ??
      source['shipment_id'] ??
      source['shipment_number'] ??
      source['shipment_Id'];

    const normalizedWebOrderId =
      webOrderId === null || webOrderId === undefined ? '' : String(webOrderId).trim();
    const normalizedShipmentId =
      shipmentId === null || shipmentId === undefined ? '' : String(shipmentId).trim();

    if (normalizedWebOrderId && normalizedShipmentId) {
      return `${normalizedWebOrderId} - ${normalizedShipmentId}`;
    }

    if (normalizedWebOrderId) {
      return normalizedWebOrderId;
    }

    if (normalizedShipmentId) {
      return normalizedShipmentId;
    }

    return '-';
  }

  isCreditOnlyRma(rmaType: string | null | undefined): boolean {
    return (rmaType || '').replace(/[^a-zA-Z]/g, '').toLowerCase() === 'creditonly';
  }

  getExistingRmaActivityItems(_detail: RmaDetailResponse): ExistingRmaActivityItem[] {
    return this.existingRmaActivityItems();
  }

  getExistingRmaActivityDotClass(variant: string): string {
    return `existing-rma-dot existing-rma-dot--${variant}`;
  }

  private getExistingRmaActivityVariant(status: string | undefined): string {
    const normalized = (status || '').trim().toLowerCase();
    if (normalized.includes('reject')) return 'rejected';
    if (normalized.includes('approved') || normalized.includes('complete')) return 'approved';
    if (normalized.includes('review') || normalized.includes('pending')) return 'pending';
    return 'neutral';
  }

  private getExistingRmaActivityRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object',
      );
    }

    if (response && typeof response === 'object') {
      const source = response as Record<string, unknown>;
      const items = source['items'] ?? source['activity_logs'] ?? source['activityLogs'] ?? source['logs'];
      if (Array.isArray(items)) {
        return items.filter(
          (item): item is Record<string, unknown> => !!item && typeof item === 'object',
        );
      }
    }

    return [];
  }

  private pickExistingRmaActivityString(
    source: Record<string, unknown>,
    keys: string[],
  ): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return '';
  }

  getExistingRmaStatusClass(status: string | undefined): string {
    const normalized = (status || '').trim().toLowerCase();
    if (
      normalized.includes('approved') ||
      normalized.includes('complete') ||
      normalized.includes('compliant')
    ) {
      return 'existing-rma-chip existing-rma-chip--success';
    }

    if (normalized.includes('reject') || normalized.includes('non')) {
      return 'existing-rma-chip existing-rma-chip--danger';
    }

    return 'existing-rma-chip existing-rma-chip--pending';
  }

  getExistingRmaChannel(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    return String(order?.['sales_channel_code'] ?? source['sales_channel_code'] ?? '-');
  }

  getExistingRmaDcc(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    return String(order?.['dcc_code'] ?? source['dcc_code'] ?? '-');
  }

  getExistingRmaWarehouse(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    return String(order?.['warehouse_code'] ?? source['warehouse_code'] ?? '-');
  }

  private toLabel(value: string): string {
    return value
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatExistingRmaDate(value: string | undefined): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return this.getDateTime(parsed);
  }

  private getAllAttachmentFiles(): File[] {
    const seen = new Set<string>();
    const files: File[] = [];

    for (const section of this.returnSections()) {
      for (const file of section.getAttachmentFiles()) {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (seen.has(key)) {
          continue;
        }

        seen.add(key);
        files.push(file);
      }
    }

    return files;
  }

  private resetAfterSuccessfulSubmission() {
    const defaultChannel = this.defaultChannelValue();

    this.searchForm.patchValue({
      channel: defaultChannel,
      startDate: '',
      endDate: '',
      orderNumber: '',
    });

    this.currentStep.set(1);
    this.selectedOrderId.set(null);
    this.selectedOrder.set(null);
    this.activeStartReturnOrderKey = null;
    this.existingRmaImeiRequestId += 1;
    this.existingRmaDeviceImeis.set([]);
    this.resetStartReturnDraftState();
    this.existingRmaMessage.set('');
    this.existingRmasDatasource.setFilter({});

    const regionId = this.activeRegionId();
    this.matchingOrdersDatasource.setFilter({
      orderNumber: '',
      startDate: '',
      endDate: '',
      salesChannelCode:
        defaultChannel && defaultChannel !== RmaCreatePage.ALL_CHANNEL_VALUE ? defaultChannel : null,
      regionId,
      includeRmaCount: true,
    });
  }

  private buildStartReturnOrderKey(order: OrderHistorySummary): string {
    return [order.orderId, order.webOrderId || '', order.shipmentId || ''].join('|');
  }

  private getNextReturnSectionId(): number {
    this.returnSectionIdSeed += 1;
    return this.returnSectionIdSeed;
  }

  private resetStartReturnDraftState() {
    this.startReturnType.set('regular');
    this.orderHistoryDetailItems.set([]);
    this.defaultOrderHistoryDetailItems.set([]);
    this.selectedDeviceIds.set([]);
    this.creditInputByDeviceId.set({});
    this.creditValueByDeviceId.set({});
    this.creditValidationByDeviceId.set({});
    this.bulkCreditValue.set('');
    this.selectedAttachmentFiles.set([]);
    this.attachmentErrorMessage.set('');
    this.isAttachmentDragActive.set(false);
    this.devicesMessage.set('');
    this.returnSectionIds.set([this.getNextReturnSectionId()]);
    this.selectedDeviceImeisByReturn.set({});
    this.reviewReturnSections.set([]);
    this.refreshCreditValuesTable();
  }

  private resetForRegionChange() {
    this.currentStep.set(1);
    this.selectedOrderId.set(null);
    this.selectedOrder.set(null);
    this.activeStartReturnOrderKey = null;
    this.existingRmaImeiRequestId += 1;
    this.existingRmaDeviceImeis.set([]);
    this.existingRmaMessage.set('');
    this.existingRmasDatasource.setFilter({});
    this.searchForm.reset({
      channel: this.defaultChannelValue(),
      startDate: '',
      endDate: '',
      orderNumber: '',
    });
    this.resetStartReturnDraftState();
  }

  private normalizeImei(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private async loadExistingRmaImeiBlocklist(atbId: number) {
    const requestId = ++this.existingRmaImeiRequestId;

    try {
      const firstPage = await this.rmaService.getRmasByAtbOrder({
        atb_Id: atbId,
        pageNumber: 1,
        pageSize: 10,
        sortBy: 'created_on',
        sortDirection: 'desc',
      });

      if (requestId !== this.existingRmaImeiRequestId) {
        return;
      }

      this.existingRmasFirstPageCache = { atbId, response: firstPage };

      const existingRmas = [...firstPage.items];
      const pageSize = Math.max(firstPage.page_size || 10, 1);
      const totalPages = Math.max(1, Math.ceil(firstPage.total_count / pageSize));

      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        const pageResponses = await Promise.all(
          remainingPages.map((pageNumber) =>
            this.rmaService.getRmasByAtbOrder({
              atb_Id: atbId,
              pageNumber,
              pageSize,
              sortBy: 'created_on',
              sortDirection: 'desc',
            }),
          ),
        );

        if (requestId !== this.existingRmaImeiRequestId) {
          return;
        }

        for (const response of pageResponses) {
          existingRmas.push(...response.items);
        }
      }

      if (existingRmas.length === 0) {
        this.existingRmaDeviceImeis.set([]);
        return;
      }

      const imeiSet = new Set<string>();
      for (const rma of existingRmas) {
        for (const item of rma.rma_items || []) {
          const normalized = this.normalizeImei(item.serial_number);
          if (normalized && normalized !== '-') {
            imeiSet.add(normalized);
          }
        }
      }

      this.existingRmaDeviceImeis.set([...imeiSet]);
    } catch {
      if (requestId === this.existingRmaImeiRequestId) {
        this.existingRmaDeviceImeis.set([]);
      }
    }
  }

  private resolveExistingRmaDeviceName(item: RmaDetailItemResponse): string {
    const source = item as unknown as { [key: string]: unknown };
    const value = item.sku_description ?? source['skuDescription'];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }

    return item.sku || '-';
  }

  private async loadReturnReasonOptions() {
    try {
      const mappings = await this.rmaService.getRmaReturnReasons();
      const options = mappings
        .map((mapping) => this.mapReturnReasonOption(mapping))
        .filter((option): option is { label: string; value: string } => !!option);

      this.reasonOptions.set(options);
    } catch {
      this.reasonOptions.set([]);
    }
  }

  private mapReturnReasonOption(mapping: ConfigMappingResponse): { label: string; value: string } | null {
    const source = mapping as Record<string, unknown>;
    const rawValue = mapping.map_value ?? source['mapping_value'] ?? source['value'];
    const value = String(rawValue ?? '').trim();

    return value ? { label: value, value } : null;
  }

  private buildCreateRmaPayload(): CreateRmaRequest | null {
    const selectedOrder = this.selectedOrder();
    if (!selectedOrder) {
      return null;
    }

    const sections = this.reviewReturnSections();
    const items: SubmitRmaItem[] = [];
    const rmaType = this.startReturnType() === 'credit' ? 'Credit Only' : 'Regular';
    const isCreditOnly = rmaType === 'Credit Only';

    for (const section of sections) {
      for (const device of section.devices) {
        const itemCreditAmount = isCreditOnly ? this.parseRmaAmount(device.creditValue) : null;

        items.push({
          sku: device.sku,
          serial_number: device.imei && device.imei !== '-' ? device.imei : '',
          reason_code: section.reason,
          comments: section.comments,
          capacity: device.capacity,
          unit_price: device.unitPrice,
          credit_amount: itemCreditAmount === null ? null : Number(itemCreditAmount.toFixed(2)),
          sku_description: device.skuDescription,
        });
      }
    }

    if (items.length === 0) {
      return null;
    }

    const creditAmount =
      isCreditOnly
        ? Number(items.reduce((sum, item) => sum + (item.credit_amount ?? 0), 0).toFixed(2))
        : null;

    return {
      atb_id: selectedOrder.orderId,
      order_number: selectedOrder.webOrderId || '',
      shipment_number: selectedOrder.shipmentId || '',
      rma_type: rmaType,
      reason: sections[0]?.reason || 'Return request',
      credit_amount: creditAmount,
      items,
    };
  }

  addReturnSection() {
    this.returnSectionIds.update((sections) => {
      const nextId = this.getNextReturnSectionId();
      return [...sections, nextId];
    });
  }

  removeReturnSection(sectionId: number) {
    this.selectedDeviceImeisByReturn.update((selected) => {
      const next = { ...selected };
      delete next[sectionId];
      return next;
    });
    this.returnSectionIds.update((sections) => {
      if (sections.length <= 1) {
        return sections;
      }

      return sections.filter((id) => id !== sectionId);
    });
  }

  updateSelectedDeviceImeis(sectionId: number, imeis: string[]) {
    this.selectedDeviceImeisByReturn.update((selected) => ({
      ...selected,
      [sectionId]: imeis,
    }));
  }

  getDisabledImeisForReturn(sectionIndex: number): string[] {
    const sectionId = this.returnSectionIds()[sectionIndex];
    return this.disabledImeisByReturn()[sectionId] || [];
  }

  getReturnSectionDeviceRows(): ReturnDeviceRow[] {
    return this.returnSectionDeviceRows();
  }

  toggleDeviceSelection(deviceId: string, checked: boolean) {
    const current = this.selectedDeviceIds();
    const next = checked
      ? [...new Set([...current, deviceId])]
      : current.filter((id) => id !== deviceId);

    this.selectedDeviceIds.set(next);
    this.refreshCreditValuesTable();
  }

  isDeviceSelected(deviceId: string): boolean {
    return this.selectedDeviceIds().includes(deviceId);
  }

  selectAllDevices() {
    this.selectedDeviceIds.set(this.getReturnDeviceRows().map((row) => row.id));
    this.refreshCreditValuesTable();
  }

  clearSelectedDevices() {
    this.selectedDeviceIds.set([]);
    this.creditInputByDeviceId.set({});
    this.creditValueByDeviceId.set({});
    this.creditValidationByDeviceId.set({});
    this.refreshCreditValuesTable();
  }

  getSelectedDeviceRows(): ReturnDeviceRow[] {
    const selected = new Set(this.selectedDeviceIds());
    return this.getReturnDeviceRows().filter((row) => selected.has(row.id));
  }

  getCreditValue(deviceId: string): string {
    const inputValue = this.creditInputByDeviceId()[deviceId];
    if (typeof inputValue === 'string') {
      return inputValue;
    }

    const value = this.creditValueByDeviceId()[deviceId];
    return typeof value === 'number' ? String(value) : '';
  }

  onCreditValueChange(deviceId: string, value: string) {
    this.creditInputByDeviceId.update((current) => ({
      ...current,
      [deviceId]: value,
    }));

    if (value.trim() === '') {
      this.creditValueByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      this.creditValidationByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      return;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      this.creditValidationByDeviceId.update((current) => ({
        ...current,
        [deviceId]: 'Enter a valid non-negative amount.',
      }));
      this.creditValueByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      return;
    }

    const maxAllowed = this.getSellPriceAmountByDeviceId(deviceId);
    if (numericValue > maxAllowed) {
      this.creditValidationByDeviceId.update((current) => ({
        ...current,
        [deviceId]: `Credit value cannot exceed selling price (${this.formatRmaValue(maxAllowed)}).`,
      }));
      this.creditValueByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      return;
    }

    this.creditValidationByDeviceId.update((current) => {
      const next = { ...current };
      delete next[deviceId];
      return next;
    });

    const safeValue = numericValue;
    this.creditValueByDeviceId.update((current) => ({
      ...current,
      [deviceId]: safeValue,
    }));
  }

  onBulkCreditValueChange(value: string) {
    this.bulkCreditValue.set(value);
  }

  applyBulkCreditValue() {
    const numericValue = Number(this.bulkCreditValue());
    const safeValue = Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
    const selectedRows = this.getSelectedDeviceRows();
    const updates = Object.fromEntries(
      selectedRows
        .filter((row) => safeValue <= row.sellPriceAmount)
        .map((row) => [row.id, safeValue]),
    );

    this.creditInputByDeviceId.update((current) => ({
      ...current,
      ...Object.fromEntries(selectedRows.map((row) => [row.id, String(safeValue)])),
    }));

    this.creditValidationByDeviceId.update((current) => {
      const next = { ...current };
      for (const row of selectedRows) {
        if (safeValue > row.sellPriceAmount) {
          next[row.id] = `Credit value cannot exceed selling price (${this.formatRmaValue(row.sellPriceAmount)}).`;
        } else {
          delete next[row.id];
        }
      }
      return next;
    });

    this.creditValueByDeviceId.update((current) => ({
      ...current,
      ...updates,
    }));
  }

  handleAttachmentFilesAttached(files: FileList | File[] | null | undefined) {
    if (!files || files.length === 0) {
      return;
    }

    const currentFiles = this.selectedAttachmentFiles();
    const nextFiles = [...currentFiles];
    const validationErrors: string[] = [];

    for (const file of Array.from(files)) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      if (!this.attachmentAllowedFileExtensions.includes(extension)) {
        validationErrors.push(`${file.name}: unsupported file format.`);
        continue;
      }

      if (file.size > this.maxAttachmentSizeBytes) {
        validationErrors.push(`${file.name}: file size exceeds 10MB.`);
        continue;
      }

      const alreadyAttached = nextFiles.some((existingFile) =>
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified,
      );

      if (!alreadyAttached) {
        nextFiles.push(file);
      }
    }

    this.selectedAttachmentFiles.set(nextFiles);
    this.attachmentErrorMessage.set(validationErrors.join(' '));
  }

  handleAttachmentFileInputChange(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.handleAttachmentFilesAttached(target?.files);

    if (target) {
      target.value = '';
    }
  }

  handleAttachmentDragOver(event: DragEvent) {
    event.preventDefault();
    this.isAttachmentDragActive.set(true);
  }

  handleAttachmentDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isAttachmentDragActive.set(false);
  }

  handleAttachmentDrop(event: DragEvent) {
    event.preventDefault();
    this.isAttachmentDragActive.set(false);
    this.handleAttachmentFilesAttached(event.dataTransfer?.files);
  }

  removeAttachmentFile(index: number) {
    this.selectedAttachmentFiles.update((files) => files.filter((_, currentIndex) => currentIndex !== index));
    this.attachmentErrorMessage.set('');
  }

  clearAttachmentFiles() {
    this.selectedAttachmentFiles.set([]);
    this.attachmentErrorMessage.set('');
  }

  getAttachmentFileType(file: File): string {
    const extension = file.name.split('.').pop()?.toUpperCase();
    return extension || 'UNKNOWN';
  }

  formatAttachmentFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  getCreditValidationMessage(deviceId: string): string {
    return this.creditValidationByDeviceId()[deviceId] || '';
  }

  getTotalCreditValue(): string {
    const total = this.getSelectedDeviceRows().reduce((sum, row) => {
      const value = this.creditValueByDeviceId()[row.id] || 0;
      return sum + value;
    }, 0);

    return total.toFixed(2);
  }

  private mapExistingRmaRow(item: RmaByAtbOrderItemResponse): ExistingRmaRow {
    return {
      rmaId: item.rma_id,
      rmaNumber: item.rma_number,
      type: item.rma_type,
      created: this.getDateTime(new Date(item.created_on)),
      reason: item.reason,
      value: this.formatRmaValue(item.credit_amount),
      status: item.status,
    };
  }

  formatRmaValue(value: number | null): string {
    if (value == null) {
      return '-';
    }

    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  private parseRmaAmount(value: string | number | null | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    const normalized = String(value || '').replace(/[^0-9.-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private async loadOrderHistoryDetails(payload: OrderDevicesRequest) {
    this.deviceListLoading.set(true);

    try {
      const details = await firstValueFrom(this.orders.getOrderHistoryDetails(payload));
      this.orderHistoryDetailItems.set(details || []);
      this.devicesMessage.set((details || []).length ? '' : 'No order history details found for the selected order.');
    } catch {
      this.devicesMessage.set('Unable to load devices for the selected order.');
    } finally {
      this.deviceListLoading.set(false);
    }
  }

  private getReturnDeviceRows(): ReturnDeviceRow[] {
    const details = this.orderHistoryDetailItems();
    if (!details.length) {
      return [];
    }

    return details.map((item) => ({
      id: this.buildReturnDeviceRowId(item),
      device: item.modelName || '-',
      imei: item.serialNumber || '-',
      sku: item.sku || '-',
      sellPrice: this.formatRmaValue(item.unitSellingPrice),
      sellPriceAmount: Number(item.unitSellingPrice) || 0,
      capacity: item.capacity || '',
      unitPrice: Number(item.unitSellingPrice) || 0,
      skuDescription: item.skuDescription || '',
    }));
  }

  private buildReturnDeviceRowId(item: OrderHistoryDetailItem): string {
    const fields = [
      item.sku,
      item.serialNumber,
      item.modelName,
      item.modelNumber,
      item.capacity,
      item.shippingNumber,
      item.trackingNumber,
      item.lpnNumber,
      item.parentLpnNumber,
      item.arInvoiceNumber,
      String(item.unitSellingPrice ?? ''),
      String(item.quantity ?? ''),
    ];

    return fields
      .map((value) => (value ?? '').toString().trim())
      .join('|')
      .toLowerCase();
  }

  private getSellPriceAmountByDeviceId(deviceId: string): number {
    const row = this.getReturnDeviceRows().find((item) => item.id === deviceId);
    return row?.sellPriceAmount ?? 0;
  }

  private getExistingRmaErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const payload = error.error;

      if (typeof payload === 'string' && payload.trim().length > 0) {
        return payload;
      }

      if (
        payload &&
        typeof payload === 'object' &&
        'message' in payload &&
        typeof payload.message === 'string' &&
        payload.message.trim().length > 0
      ) {
        return payload.message;
      }

      if (typeof error.message === 'string' && error.message.trim().length > 0) {
        return error.message;
      }
    }

    return 'No existing RMA details found for the selected order.';
  }

  private refreshCreditValuesTable() {
    this.creditValuesDatasource.setFilter({ refreshAt: Date.now() });
  }
}



